<template>
  <div class="pa-0 mx-md-auto justify-center flex-column profile-personal-data">
    <div class="form-title mb-3">
      {{ t("Personal Data") }}
    </div>
    <v-form
      ref="form"
      v-model="valid"
    >
      <div class="profile-row edit">
        <Button
          class="col-6"
          @click="userEditDialog = true"
        >
          <v-icon start>
            mdi-account-edit-outline
          </v-icon>
          {{ t("Edit") }}
        </Button>
      </div>
      <div class="profile-row">
        <InputText
          v-model="props.userProfile.salutation"
          class="col-6 pl-0 pr-2"
          :label="t('Salutation')"
          readonly
        />
        <InputText
          v-model="props.userProfile.title"
          class="col-6 pl-2 pr-0"
          :label="t('Title')"
          readonly
        />
      </div>
      <div class="profile-row">
        <InputText
          v-model="props.userProfile.firstName"
          class="col-6 pl-0 pr-2"
          :label="t('First name')"
          readonly
        />
        <InputText
          v-model="props.userProfile.lastName"
          class="col-6 pl-2 pr-0"
          :label="t('Last name')"
          readonly
        />
      </div>
      <InputText
        v-model="language"
        :label="t('Preferred language')"
        readonly
      />
      <InputText
        v-model="props.userProfile.physicianNpiNumber"
        :label="t('Physician NPI Number')"
        readonly
      />
      <InputText
        v-model="props.userProfile.institution"
        :label="t('Hospital/Practice name')"
        readonly
      />
      <InputText
        v-model="props.userProfile.department"
        :label="t('Department')"
        readonly
      />
      <InputText
        v-model="props.userProfile.position"
        :label="t('Position')"
        readonly
      />
      <InputText
        v-model="props.userProfile.street"
        class="d-flex mb-4"
        :label="t('Street, number')"
        readonly
      />
      <div class="profile-row">
        <InputText
          v-model="props.userProfile.postalCode"
          class="col-6 pl-0 pr-2"
          :label="t('Postal code')"
          readonly
        />
        <InputText
          v-model="props.userProfile.city"
          class="col-6 pl-2 pr-0"
          :label="t('City')"
          readonly
        />
      </div>
      <InputText
        v-model="country"
        :label="t('Country')"
        readonly
      />
      <div class="profile-row next">
        <Button
          class="col-6"
          :scroll="true"
          @click="$emit('goto', 'authentication')"
        >
          {{ t("Next") }}
          <v-icon end>
            mdi-chevron-right
          </v-icon>
        </Button>
      </div>
    </v-form>
    <v-dialog
      v-model="userEditDialog"
      max-width="890px"
      :attach="true"
    >
      <template #default>
        <v-card>
          <v-card-title>
            <v-spacer />
            <Button
              :icon-btn="true"
              color="text-black"
              @click="
                changes
                  ? (closeEditDialogConfirmation = true)
                  : (userEditDialog = false)
              "
            >
              <v-icon>mdi-close</v-icon>
            </Button>
          </v-card-title>
          <iframe
            :src="accountPage"
            :title="t('Personal Data')"
            width="100%"
            height="800"
          />
        </v-card>
      </template>
    </v-dialog>
    <ConfirmationDialog
      v-model="closeEditDialogConfirmation"
      :title="t('Unsaved changes')"
      :message="
        t(
          'Are you sure you want to close the dialog? Unsaved changes will be lost.'
        )
      "
      confirm-text="Close"
      cancel-text="Cancel"
      @confirm="(userEditDialog = false), (changes = false)"
      @cancel="closeEditDialogConfirmation = false"
    />
  </div>
</template>

<script lang="ts" setup>
import Button from "@/components/input-components/Button.vue";
import InputText from "@/components/input-components/InputText.vue";
import Config from "@/config/config";
import ConfirmationDialog from "@/components/dialogs/ConfirmationDialog.vue";
import { ref, watch, computed, onMounted, onBeforeUnmount, reactive } from "vue";
import { useI18n } from 'vue-i18n'
import {Country} from '@/entities/country'

defineOptions({
  name: 'ProfilePersonalData'
})

const { t, locale } = useI18n();

const props = defineProps<{
  userProfile: Record<string, any>,
  countries: Array<Country>
}>();
const localUserProfile = reactive({ ...props.userProfile });

watch(() => props.userProfile, (newVal) => {
  Object.assign(localUserProfile, newVal);
});

const emit = defineEmits<{
  goto: [section: string]
  reloadProfile: []
}>();

const valid = ref(false);
const userEditDialog = ref(false);
const closeEditDialogConfirmation = ref(false);
const changes = ref(false);

const accountPage = computed(() => {
  if (userEditDialog.value) {
    const url = new URL("account/", Config.KEYCLOAK_REALM_URL);
    url.searchParams.append("kc_locale", locale.value);
    const parentUrl = new URL(parent.document.URL);
    url.searchParams.append("url", parentUrl.origin);
    return url.href;
  }
  return "about:blank";
});

const language = computed((): string => {
    if (!props.userProfile?.locale) {
      return "";
    }

    return t("locale_" + props.userProfile.locale) ? t("locale_" + props.userProfile.locale) : props.userProfile.locale
});

const country = computed((): string => props.countries.find((country: Country) => country.countryId === props.userProfile.country)?.name || props.userProfile.country);

const checkChanges = (event: MessageEvent) => {
  if (event.data === "changed") {
    changes.value = true;
  } else if (event.data === "saved") {
    changes.value = false;
    setTimeout(() => (userEditDialog.value = false), 1000);
  } else if (event.data === "closeDialog") {
    changes.value = false;
    userEditDialog.value = false;
  }
};

onMounted(() => window.addEventListener("message", checkChanges));
onBeforeUnmount(() => window.removeEventListener("message", checkChanges));

watch(userEditDialog, (newVal) => {
  if (!newVal) {
    emit("reloadProfile");
  }
});

function onConfirmClose() {
  userEditDialog.value = false;
  changes.value = false;
  closeEditDialogConfirmation.value = false;
}
</script>

<style lang="scss">
.profile-personal-data {
  .profile-row,
  .input-wrapper {
    display: flex;
    height: 4.375rem;
  }

  .profile-row.edit {
    margin-top: 1.5625rem;
  }

  .profile-row.next {
    justify-content: flex-end;
  }

  .col-6 {
    padding: 0;
  }

  v-dialog {
    position: relative;
    z-index: 502 !important;
  }

  button {
    min-width: 184px;
  }
}
</style>
